import { CheckCircle2, Download, FileLock2, Loader, Redo2, Send, Undo2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import { PremiumBadge } from './PremiumBadge';
import { SendForReviewDialog } from './SendForReviewDialog';

interface SidebarFooterProps {
  templateSlug?: string;
  documentId?: string | null;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  isEditable: boolean;
  signatureFieldCount: number;
  isPremiumUnlocked: boolean;
  onSendToSignature: () => void;
  onRequestDownload: () => void;
  /**
   * Downloads an image-only copy with no extractable text. Optional on purpose:
   * omitting it hides the button entirely. When this was required, the two
   * fixed-DOCX reviews rendered the footer without it and shipped a button whose
   * onClick was `undefined` — it looked enabled and did nothing. Nothing caught
   * it, because `tsc` can't run here (TS 4.9 against a TS5 tsconfig) and Vite's
   * esbuild strips types without checking them. A missing handler now fails
   * visibly instead of silently.
   */
  onRequestProtectedDownload?: () => void;
  /** A download is in flight — swaps the icon for a spinner and blocks re-clicks. */
  isDownloading?: boolean;
  /** The protected (rasterised) download is in flight. */
  isDownloadingProtected?: boolean;
  hideDownload?: boolean;
  isApprovalPage?: boolean;
  isContractApproved?: boolean;
  isPendingApproval?: boolean;
}

/**
 * Pinned bottom of the review sidebar — the edit-history undo/redo controls
 * (shown only when there's history) plus the Send-for-signature / Download
 * actions. Hidden by the caller while the block editor is open.
 */
export function SidebarFooter({
  templateSlug,
  documentId,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  isEditable,
  signatureFieldCount,
  isPremiumUnlocked,
  onSendToSignature,
  onRequestDownload,
  onRequestProtectedDownload,
  isDownloading = false,
  isDownloadingProtected = false,
  hideDownload = false,
  isApprovalPage = false,
  isContractApproved = false,
  isPendingApproval = false,
}: SidebarFooterProps) {
  const { t } = useTranslation();
  const [isSendToSignatureModalOpen, setIsSendToSignatureModalOpen] = useState(false);

  return (
    <>
      <div className="border-t border-border bg-[hsl(var(--bg))] px-4 pt-3">
        {(canUndo || canRedo) && (
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs text-globe-grey">
              {t('GENERATEREVIEW_HISTORY_LABEL', { defaultValue: 'Edit history' })}
            </span>
            <div className="flex items-center border border-border rounded-md bg-[hsl(var(--bg))] divide-x divide-border">
              <button
                onClick={onUndo}
                disabled={!canUndo}
                className="p-1.5 text-[hsl(var(--fg))] hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors rounded-l-md"
                aria-label="Undo (Ctrl+Z)"
                title="Undo (Ctrl+Z)"
              >
                <Undo2 className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={onRedo}
                disabled={!canRedo}
                className="p-1.5 text-[hsl(var(--fg))] hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-colors rounded-r-md"
                aria-label="Redo (Ctrl+Shift+Z)"
                title="Redo (Ctrl+Shift+Z)"
              >
                <Redo2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-center gap-3">
          <div className={hideDownload ? 'flex w-full' : ' flex gap-3'}>
            {isEditable && signatureFieldCount > 0 && (
              <Button
                className={hideDownload ? 'w-full' : 'flex-1'}
                size="sm"
                onClick={() => {
                  if (isApprovalPage || isContractApproved) {
                    onSendToSignature();
                  } else {
                    setIsSendToSignatureModalOpen(true);
                  }
                }}
                variant="primary"
                disabled={isPendingApproval}
                title={
                  isPendingApproval
                    ? t('GENERATEREVIEW_PENDING_APPROVAL_SIGNATURE_DISABLED', {
                      defaultValue:
                        'The contract must be approved before it can be sent for signature.',
                    })
                    : undefined
                }
              >
                {isApprovalPage && !isContractApproved ? (
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                {isApprovalPage && !isContractApproved
                  ? t('GENERATEREVIEW_ACTIONS_APPROVECONTRACT', {
                    defaultValue: 'Approve Contract',
                  })
                  : t('GENERATEREVIEW_ACTIONS_SENDFORSIGNATURE')}
              </Button>
            )}
            {!hideDownload && (
              <Button
                className="flex-1"
                onClick={onRequestDownload}
                variant="outline"
                size="sm"
                disabled={isDownloading}
                aria-busy={isDownloading}
              >
                {isDownloading ? (
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                {isDownloading
                  ? t('GENERATEREVIEW_ACTIONS_PREPARINGPDF', { defaultValue: 'Preparing…' })
                  : t('GENERATEREVIEW_ACTIONS_DOWNLOADPDF')}
                <PremiumBadge unlocked={isPremiumUnlocked} />
              </Button>
            )}
          </div>
          {/*
          Secondary export on its own row: an image-only copy that automated
          readers can't lift text from. Rasterising every page takes noticeably
          longer than the normal download, hence its own busy state.
        */}
          {!hideDownload && onRequestProtectedDownload && (
            <div className="flex w-full flex-1">
              <Button
                className="w-full"
                onClick={onRequestProtectedDownload}
                variant="outline"
                size="sm"
                disabled={isDownloadingProtected}
                aria-busy={isDownloadingProtected}
                title={t('GENERATEREVIEW_ACTIONS_DOWNLOADPROTECTEDPDF_HINT', {
                  defaultValue:
                    'Downloads a copy with no selectable or machine-readable text. Readable by people, not by text extractors.',
                })}
              >
                {isDownloadingProtected ? (
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <FileLock2 className="w-4 h-4 mr-2" />
                )}
                {isDownloadingProtected
                  ? t('GENERATEREVIEW_ACTIONS_PREPARINGPROTECTEDPDF', {
                    defaultValue: 'Protecting…',
                  })
                  : t('GENERATEREVIEW_ACTIONS_DOWNLOADPROTECTEDPDF', {
                    defaultValue: "Download Protected PDF",
                  })}
                <PremiumBadge unlocked={isPremiumUnlocked} />
              </Button>
            </div>
          )}
        </div >
      </div>
      {!isApprovalPage && !isContractApproved && templateSlug && documentId && (
        <SendForReviewDialog
          open={isSendToSignatureModalOpen}
          onOpenChange={setIsSendToSignatureModalOpen}
          onConfirm={onSendToSignature}
          templateSlug={templateSlug}
          documentId={documentId}
        />
      )
      }
    </>
  );
}
